
import React, { useEffect, useState } from 'react';
import { Moon, Sun, Orbit, Users, Loader2 } from 'lucide-react';
import { supabase, fetchMemberships } from '../services/geminiService';

interface Member {
  name: string;
  location: string;
  tier: string;
}

const TierConfig: Record<string, { icon: any, color: string, glow: string }> = {
  'Lunar Seeker': { icon: Moon, color: 'text-slate-400', glow: 'bg-slate-400/20 shadow-[0_0_15px_rgba(148,163,184,0.3)]' },
  'Solar Initiate': { icon: Sun, color: 'text-amber-400', glow: 'bg-amber-400/20 shadow-[0_0_15px_rgba(245,158,11,0.3)]' },
  'Galactic Master': { icon: Orbit, color: 'text-indigo-400', glow: 'bg-indigo-400/20 shadow-[0_0_15px_rgba(99,102,241,0.3)]' },
};

const MembershipMembers: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchMemberships();
      setMembers(data as Member[]);
      setLoading(false);
    };

    loadData();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('public:memberships')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'memberships' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setMembers(prev => [payload.new as Member, ...prev]);
        } else if (payload.eventType === 'DELETE') {
          setMembers(prev => prev.filter(m => (m as any).id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
          setMembers(prev => prev.map(m => (m as any).id === payload.new.id ? (payload.new as Member) : m));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getMembersByTier = (tierName: string) => {
    return members.filter(m => m.tier === tierName);
  };

  return (
    <section id="community" className="py-24 bg-slate-950/60 relative overflow-hidden backdrop-blur-md border-y border-indigo-900/20">
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-2">Soul Registry</h2>
          <h3 id="community-title" className="text-4xl md:text-5xl font-serif text-slate-50">Radiant Community</h3>
          <div className="w-24 h-1 bg-amber-500 mx-auto mt-6 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
          <p className="mt-6 text-indigo-300/60 max-w-2xl mx-auto font-light leading-relaxed italic">
            "A constellation of seekers, unified by the study of the stars and the pursuit of inner light."
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="animate-spin text-amber-500 mb-4" size={40} />
            <p className="text-[10px] uppercase tracking-widest text-indigo-500">Syncing with the heavens...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {['Lunar Seeker', 'Solar Initiate', 'Galactic Master'].map((tier) => {
              const tierMembers = getMembersByTier(tier);
              const Config = TierConfig[tier];
              const Icon = Config.icon;
              return (
                <div key={tier} className="flex flex-col animate-fade-in">
                  <div className="flex items-center gap-4 mb-8 pb-4 border-b border-indigo-900/30">
                    <div className={`p-2.5 rounded-full ${Config.glow}`}>
                      <Icon size={20} className={Config.color} />
                    </div>
                    <div>
                      <h4 className={`text-sm uppercase tracking-[0.3em] font-bold ${Config.color}`}>{tier}</h4>
                      <p className="text-[10px] text-indigo-500 uppercase tracking-widest">{tierMembers.length} Active Souls</p>
                    </div>
                  </div>

                  <div className="space-y-6 min-h-[100px]">
                    {tierMembers.length === 0 ? (
                      <div className="text-center py-8 opacity-20 border border-dashed border-indigo-900/50 rounded-sm">
                        <p className="text-[9px] uppercase tracking-widest text-indigo-400">Awaiting Seekers</p>
                      </div>
                    ) : (
                      tierMembers.map((member, i) => (
                        <div 
                          key={i} 
                          className="group flex items-center gap-4 animate-fade-in"
                        >
                          <div className={`w-10 h-10 rounded-full border border-indigo-900/50 flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:border-amber-500/50 ${Config.glow.replace('20', '5')}`}>
                            <div className={`w-2 h-2 rounded-full ${Config.color} animate-pulse`} />
                          </div>
                          <div>
                            <p className="text-sm text-indigo-100 font-serif tracking-wide group-hover:text-white transition-colors">
                              {member.name}
                            </p>
                            <p className="text-[10px] text-indigo-500 uppercase tracking-widest flex items-center gap-1">
                               {member.location}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Decorative Element */}
                  <div className="mt-12 flex justify-center opacity-10">
                     <Users size={60} className={Config.color} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Subtle Bottom Glow */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent shadow-[0_0_15px_rgba(245,158,11,0.2)]" />
    </section>
  );
};

export default MembershipMembers;
